import { useCallback, useEffect, useRef } from 'react'
import * as cache from 'state/cache'
import CableApp from 'actioncable'
import { withChannelAuth } from 'state/channels/channelAuth'
import useStore from 'state/knovStore'
import api from 'api/api'
import { getAnswerBatched } from 'api/answerBatchFetch'
import { JobLifecycle } from 'types/jobLifecycle'
import type { AnswerOrDraft } from 'types/answers'
import { cacheViewerNeutralAnswerUpdate } from 'state/viewerNeutralAnswerUpdate'
import { reconcileAnswerOnSubscribe } from './reconcileOnSubscribe'

const REPORT = false
const pendingPollAnswerRefreshes = new Set<string>()

enum MsgTypes {
    UPDATE_ANSWER = 'update answer',
    UPDATE_JOB_STATUS = 'update job status',
}

/** Shape of an ActionCable subscription (no upstream types available). */
interface ActionCableSubscription {
    perform(action: string, data?: Record<string, unknown>): boolean
    send(data: Record<string, unknown>): boolean
    unsubscribe(): void
    consumer?: {
        connection?: { isOpen(): boolean }
        subscriptions?: { subscriptions?: ActionCableSubscription[] }
    }
}

/** Data received on the AnswerChannel websocket. */
interface AnswerChannelData {
    message?: string
    type?: string
    answer?: AnswerOrDraft
    job_status?: {
        status: string
        run_id?: string
        timestamp?: number
        [key: string]: unknown
    }
    [key: string]: unknown
}

type OtherMessageHandler = (data: AnswerChannelData) => void
type IncomingJobStatus = Partial<NonNullable<AnswerChannelData['job_status']>>
type UseAnswerChannelOpts = {
    autoSubscribe?: boolean
    reconcileOnSubscribe?: boolean
    deferReconcileOnSubscribe?: boolean
}

type ChannelEntry = {
    channel: ActionCableSubscription | null
    refCount: number
    otherHandlers: Set<OtherMessageHandler>
}

function pollPayloadNeedsPersonalizedRefresh(answer?: AnswerOrDraft | null): boolean {
    if (!answer || answer.message_type !== 'poll' || answer.poll_open === false) return false
    if (answer.poll_results_visible !== false) return false

    const options = Array.isArray(answer.poll_options_payload) ? answer.poll_options_payload : []
    if (!options.length) return false

    return options.every(option => option.votes_count == null && option.percentage == null)
}

function refreshPollAnswerForUnlockedViewer(answerId: string) {
    if (pendingPollAnswerRefreshes.has(answerId)) return

    const existingAnswer = cache.getCachedAnswer(answerId)
    const viewerHasUnlockedResults =
        existingAnswer?.poll_results_visible === true ||
        existingAnswer?.poll_user_vote_option_id != null
    if (!viewerHasUnlockedResults) return

    pendingPollAnswerRefreshes.add(answerId)
    api.getAnswer(answerId)
        .then(answer => {
            if (answer) cache.cacheAnswer(answer)
        })
        .catch(() => {})
        .finally(() => {
            pendingPollAnswerRefreshes.delete(answerId)
        })
}

// Dedupe ActionCable subscriptions per answerId and allow multiple listeners.
const channelRegistry: Record<string, ChannelEntry> = {}

function presentString(value: unknown): string | undefined {
    return typeof value === 'string' && value.trim().length > 0 ? value : undefined
}

function objectValue(value: unknown): Record<string, unknown> | undefined {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : undefined
}

function jobStatusAiRunId(jobStatus: IncomingJobStatus): string | undefined {
    return presentString(jobStatus.ai_run_id) || presentString(jobStatus.run_id)
}

function mergeAiRunJobStatusIntoCachedAnswer(answerId: string, jobStatus: IncomingJobStatus) {
    const aiRunId = jobStatusAiRunId(jobStatus)
    if (!aiRunId) return

    const existingAnswer = cache.getCachedAnswer(answerId)
    const existingRuns = Array.isArray(existingAnswer?.ai_runs) ? existingAnswer.ai_runs : []
    if (!existingAnswer || existingRuns.length === 0) return

    let didUpdate = false
    const nextRuns = existingRuns.map(run => {
        const runId = presentString(run?.id)
        if (runId !== aiRunId) return run

        const errorPayload =
            objectValue(jobStatus.error_payload) ||
            (jobStatus.error_message
                ? { error_message: jobStatus.error_message }
                : run?.error_payload)

        didUpdate = true
        return {
            ...run,
            id: run?.id || aiRunId,
            status: jobStatus.status || run?.status,
            provider_job_id:
                presentString(jobStatus.provider_job_id) || run?.provider_job_id || null,
            action_index:
                typeof jobStatus.action_index === 'number'
                    ? jobStatus.action_index
                    : run?.action_index,
            generation_index:
                typeof jobStatus.generation_index === 'number'
                    ? jobStatus.generation_index
                    : run?.generation_index,
            action_tag: presentString(jobStatus.action_tag) || run?.action_tag,
            model_type: presentString(jobStatus.model_type) || run?.model_type || null,
            error_payload: errorPayload,
            updated_at: new Date().toISOString(),
        }
    })

    if (!didUpdate) return

    cache.cacheAnswer(
        {
            ...existingAnswer,
            ai_runs: nextRuns,
        },
        false,
    )
}

export default function useAnswerChannel(
    answerId: string,
    onOtherMessageOrOpts?: OtherMessageHandler | UseAnswerChannelOpts,
    maybeOpts: UseAnswerChannelOpts = {},
) {
    const onOtherMessage =
        typeof onOtherMessageOrOpts === 'function' ? onOtherMessageOrOpts : undefined
    const opts =
        typeof onOtherMessageOrOpts === 'function'
            ? maybeOpts
            : onOtherMessageOrOpts || maybeOpts || {}
    const {
        autoSubscribe = true,
        reconcileOnSubscribe = false,
        deferReconcileOnSubscribe = false,
    } = opts
    const set = useStore.getState().set
    const reconcileHandle = useRef<number | null>(null)
    const reconcileUsesIdleCallback = useRef(false)
    const onOtherMessageRef = useRef<OtherMessageHandler | undefined>(onOtherMessage)
    // Stable function identity so the registry Set doesn't get out of sync across re-renders.
    const stableOtherHandler = useRef<OtherMessageHandler | null>(null)
    if (!stableOtherHandler.current) {
        stableOtherHandler.current = (data: AnswerChannelData) => {
            try {
                onOtherMessageRef.current?.(data)
            } catch (e) {}
        }
    }

    useEffect(() => {
        onOtherMessageRef.current = onOtherMessage
    }, [onOtherMessage])

    const cancelScheduledReconcile = useCallback(() => {
        if (reconcileHandle.current == null) return
        const idleWindow = window as typeof window & {
            cancelIdleCallback?: (handle: number) => void
        }
        if (reconcileUsesIdleCallback.current && idleWindow.cancelIdleCallback) {
            idleWindow.cancelIdleCallback(reconcileHandle.current)
        } else {
            window.clearTimeout(reconcileHandle.current)
        }
        reconcileHandle.current = null
    }, [])

    const reconcile = useCallback(() => {
        if (!reconcileOnSubscribe || !answerId) return
        cancelScheduledReconcile()
        if (!deferReconcileOnSubscribe) {
            void reconcileAnswerOnSubscribe(answerId)
            return
        }

        const run = () => {
            reconcileHandle.current = null
            if (channelRegistry[answerId]) void reconcileAnswerOnSubscribe(answerId)
        }
        const idleWindow = window as typeof window & {
            requestIdleCallback?: (callback: () => void, options?: { timeout: number }) => number
        }
        if (idleWindow.requestIdleCallback) {
            reconcileUsesIdleCallback.current = true
            reconcileHandle.current = idleWindow.requestIdleCallback(run, { timeout: 1000 })
        } else {
            reconcileUsesIdleCallback.current = false
            reconcileHandle.current = window.setTimeout(run, 200)
        }
    }, [answerId, cancelScheduledReconcile, deferReconcileOnSubscribe, reconcileOnSubscribe])

    const subscribe = useCallback(() => {
        if (!answerId) return

        if (!channelRegistry[answerId]) {
            const entry: ChannelEntry = {
                channel: null,
                refCount: 0,
                otherHandlers: new Set(),
            }

            // Credentials ride along so a post in a private thread streams to its
            // viewers (AnswerChannel#subscribed); public posts never check them.
            entry.channel = CableApp.cable.subscriptions.create(
                withChannelAuth({
                    channel: 'AnswerChannel',
                    stream_from: `answer_${answerId}`,
                    answer_id: answerId,
                }),
                {
                    received: async (data: AnswerChannelData) => {
                        const { message: msgType, type } = data
                        // Check both 'message' and 'type' fields for compatibility
                        const messageType = msgType || type
                        if (messageType === MsgTypes.UPDATE_ANSWER) {
                            const needsPersonalizedRefresh = pollPayloadNeedsPersonalizedRefresh(
                                data.answer,
                            )
                            await cacheViewerNeutralAnswerUpdate(data.answer, {
                                getCachedAnswer: cache.getCachedAnswer,
                                cacheAnswer: answer => cache.cacheAnswer(answer),
                                refreshAnswer: getAnswerBatched,
                            })
                            if (needsPersonalizedRefresh) {
                                refreshPollAnswerForUnlockedViewer(answerId)
                            }
                        } else if (messageType === MsgTypes.UPDATE_JOB_STATUS) {
                            const incomingJobStatus: IncomingJobStatus = data?.job_status ?? {}
                            const incomingRunId = jobStatusAiRunId(incomingJobStatus)
                            if (presentString(incomingJobStatus.action_tag) && !incomingRunId) {
                                return
                            }
                            mergeAiRunJobStatusIntoCachedAnswer(answerId, incomingJobStatus)

                            set(state => {
                                const existingJobStatus = state.answerJobs[answerId]
                                const existingRunId = jobStatusAiRunId(existingJobStatus || {})
                                const incomingTimestamp =
                                    typeof incomingJobStatus.timestamp === 'number'
                                        ? incomingJobStatus.timestamp
                                        : undefined
                                const existingTimestamp =
                                    typeof existingJobStatus?.timestamp === 'number'
                                        ? existingJobStatus.timestamp
                                        : undefined

                                if (
                                    existingRunId &&
                                    incomingRunId &&
                                    existingRunId !== incomingRunId
                                ) {
                                    const isNewRunStart =
                                        incomingJobStatus.status === JobLifecycle.STARTED &&
                                        (typeof existingTimestamp !== 'number' ||
                                            typeof incomingTimestamp !== 'number' ||
                                            incomingTimestamp >= existingTimestamp)

                                    if (!isNewRunStart) {
                                        return
                                    }
                                }

                                if (
                                    typeof incomingTimestamp === 'number' &&
                                    typeof existingTimestamp === 'number' &&
                                    incomingTimestamp < existingTimestamp
                                ) {
                                    return
                                }

                                state.answerJobs[answerId] = {
                                    ...incomingJobStatus,
                                    status: incomingJobStatus.status as JobLifecycle,
                                }
                            })
                        } else {
                            // Forward link preview (and any other) messages to registered listeners.
                            const handlers = channelRegistry[answerId]?.otherHandlers
                            if (handlers && handlers.size > 0) {
                                handlers.forEach(fn => {
                                    try {
                                        fn(data)
                                    } catch (e) {}
                                })
                            }
                        }
                    },

                    connected() {
                        if (gon.env === 'development' && REPORT)
                            console.log(`Connected to AnswerChannel id ${answerId}`)
                    },

                    disconnected() {
                        if (gon.env === 'development' && REPORT)
                            console.log(`Disconnected from AnswerChannel id ${answerId}`)
                    },
                },
            )
            channelRegistry[answerId] = entry
        }

        const entry = channelRegistry[answerId]
        entry.refCount += 1
        if (onOtherMessageRef.current && stableOtherHandler.current)
            entry.otherHandlers.add(stableOtherHandler.current)

        if (gon.env === 'development' && REPORT)
            console.log(
                `subscribed to answer channel id ${answerId}`,
                entry.channel,
                entry.channel?.consumer?.subscriptions?.subscriptions?.length,
            )
        reconcile()
    }, [answerId, reconcile, set])

    const unsubscribe = useCallback(() => {
        cancelScheduledReconcile()
        if (!answerId) return
        const entry = channelRegistry[answerId]
        if (!entry) return

        if (stableOtherHandler.current) entry.otherHandlers.delete(stableOtherHandler.current)

        entry.refCount -= 1
        if (entry.refCount <= 0) {
            try {
                entry.channel?.unsubscribe?.()
            } catch (e) {}
            if (gon.env === 'development' && REPORT)
                console.log(
                    `unsubscribed from answer channel id ${answerId}`,
                    entry.channel,
                    entry.channel?.consumer?.subscriptions?.subscriptions?.length,
                )
            delete channelRegistry[answerId]
        }
    }, [answerId, cancelScheduledReconcile])

    useEffect(() => {
        // TODO: have this map be stored elsewhere and imported so that all
        // components can use it
        if (!autoSubscribe) return
        if (answerId) subscribe()

        return () => unsubscribe()
    }, [answerId, autoSubscribe, subscribe, unsubscribe])

    // We return this so AnswerInView or others can unsubscribe.
    return {
        subscribe,
        unsubscribe,
    }
}
